/* storybook-check-ignore */
import { Flex, Text, Button, Loader } from '@opendoor/bricks/core';
import ReserveATourSlotButton, {
  Props as ReserveATourSlotButtonProps,
} from './ReserveATourSlotButton';
import { useRequestATourContext } from './useRequestATour';
import { arrayIncludeSlot } from './util';
import { useMemo } from 'react';

interface Props {
  /**
   * Configuration for the button, default value `undefined`
   * - when `false` : on this configuration will present the suggested slots
   * to be toured and a reserve new tour time button
   * - when `true` : we are only going to present a `Request self tour` button
   * that will open an initial dialog to select one of the suggested slots,
   * this should be used on the mobile context.
   */
  isSingleButton?: boolean;
}

export default function RequestATour({ isSingleButton }: Props) {
  const { slots, closestSlots, listing, ...requestATour } = useRequestATourContext();
  const openModal: ReserveATourSlotButtonProps['onClick'] = (props) => {
    requestATour.modalOpenedFromSingleButton(!!isSingleButton);
    requestATour.openModal(props);
  };
  const noOpenHouseScheduled = useMemo(
    () => closestSlots.every(({ slotExists }) => !slotExists),
    [closestSlots],
  );
  const requestedTourSlots = useMemo(
    () => requestATour.data?.exclusive_tour_request_time_slots || [],
    [requestATour.data],
  );
  if (!closestSlots.length) return null;
  if (requestATour.isInitiallyLoadingRequestATour) {
    return (
      <Flex height="120px" alignItems="center" justifyContent="center">
        <Loader />
      </Flex>
    );
  }
  if (isSingleButton) {
    return (
      <Button
        variant="primary"
        aria-label={null}
        analyticsName="cosmos-exclusives-request-self-tour"
        onClick={() =>
          openModal({
            contextualData: { buttonText: 'Request self tour', origin: 'request-a-tour-buttons' },
          })
        }
      >
        Request self tour
      </Button>
    );
  }
  return (
    <Flex flexDirection="column" gridGap="1rem">
      <Text as="div" fontSize="s1" color="neutrals100">
        <Text fontWeight="bold">Self tour this home</Text>
        Reserve a slot in an existing tour
        {!requestATour.hasTourRequest && ' or request a time that’s better for you'}.
      </Text>
      {closestSlots.map((props, i) => (
        <ReserveATourSlotButton
          key={`reserveATourSpotButton${i}`}
          {...props}
          onClick={openModal}
          dashedBorder={noOpenHouseScheduled}
          listing={listing}
          reportEvent={requestATour.reportEvent}
          spotReserved={arrayIncludeSlot(requestedTourSlots, props.slot)}
        />
      ))}
      {!requestATour.hasTourRequest && (
        <ReserveATourSlotButton
          isPrimary={!listing.exclusive_listing_info.ongoing_and_upcoming_open_houses?.length}
          onClick={openModal}
          listing={listing}
          reportEvent={requestATour.reportEvent}
        />
      )}
      <Text color="neutrals90" textAlign="center" display={['none', 'none', 'block']}>
        No agent needed. Tour at your own pace.
      </Text>
    </Flex>
  );
}
